
import { Bold, Italic, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered, Quote, Code, Link as LinkIcon, Image as ImageIcon, Minus } from 'lucide-react';
import { Editor } from '@tiptap/react';

interface EditorToolbarProps {
  editor: Editor | null;
  /**
   * PE-2175 — lets a caller (content-pages' RichTextEditor) route the image button
   * through its own upload flow instead of a bare URL. Returns the URL to insert, or
   * null to cancel. Optional and backward-compatible: when omitted, the blog editor
   * keeps its original `window.prompt('Image URL')` behaviour untouched.
   */
  onRequestImage?: () => Promise<string | null>;
}

export const EditorToolbar = ({ editor, onRequestImage }: EditorToolbarProps) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="flex items-center space-x-1 border rounded-md p-1 bg-card text-card-foreground shadow-sm sticky top-0 z-10 flex-wrap gap-y-1">
      <button 
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={`p-1.5 rounded ${editor.isActive('bold') ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <Bold className="w-4 h-4" />
      </button>
      <button 
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={`p-1.5 rounded ${editor.isActive('italic') ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <Italic className="w-4 h-4" />
      </button>
      <button 
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={`p-1.5 rounded ${editor.isActive('strike') ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <Strikethrough className="w-4 h-4" />
      </button>
      
      <div className="w-px h-4 bg-border mx-1" />
      
      <button 
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={`p-1.5 rounded ${editor.isActive('heading', { level: 1 }) ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <Heading1 className="w-4 h-4" />
      </button>
      <button 
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`p-1.5 rounded ${editor.isActive('heading', { level: 2 }) ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <Heading2 className="w-4 h-4" />
      </button>
      <button 
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`p-1.5 rounded ${editor.isActive('heading', { level: 3 }) ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <Heading3 className="w-4 h-4" />
      </button>
      
      <div className="w-px h-4 bg-border mx-1" />
      
      <button 
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-1.5 rounded ${editor.isActive('bulletList') ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <List className="w-4 h-4" />
      </button>
      <button 
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-1.5 rounded ${editor.isActive('orderedList') ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <ListOrdered className="w-4 h-4" />
      </button>
      
      <div className="w-px h-4 bg-border mx-1" />
      
      <button 
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`p-1.5 rounded ${editor.isActive('blockquote') ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <Quote className="w-4 h-4" />
      </button>
      <button 
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`p-1.5 rounded ${editor.isActive('codeBlock') ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <Code className="w-4 h-4" />
      </button>
      
      <div className="w-px h-4 bg-border mx-1" />
      
      <button 
        onClick={() => {
          const url = window.prompt('URL');
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          } else if (url === '') {
            editor.chain().focus().unsetLink().run();
          }
        }}
        className={`p-1.5 rounded ${editor.isActive('link') ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <LinkIcon className="w-4 h-4" />
      </button>
      <button
        onClick={async () => {
          const url = onRequestImage ? await onRequestImage() : window.prompt('Image URL');
          if (url) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        }}
        className={`p-1.5 rounded ${editor.isActive('image') ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        <ImageIcon className="w-4 h-4" />
      </button>
      <button 
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground"
      >
        <Minus className="w-4 h-4" />
      </button>
    </div>
  );
};
